import React, { useState, Dispatch, SetStateAction } from 'react';
import styled from '@emotion/styled';
import isEmpty from 'lodash/isEmpty';
import Checkbox from '@atlaskit/checkbox';
import { STATSIG_FEATURE_GATES } from '@css-xp/ppc-constants';
import { CloudSiteContainer } from './components/cloud-sites-container';
import { SelectType, CloudSiteOrOrgNameMapGroupedByBillingProfile, CloudSiteOrOrgNameMap } from './types';
import { NotificationTypes, errorNotificationBuilder, useNotification } from '@css-xp/base';
import { useStore, useCloudSiteOrOrgNameMapStore } from '../../../store';
import { processEntity } from '../../entitlement-transfer/src/utils/api';
import { SearchField } from './components/search-and-filter/search-field';

interface Props {
  customerIds: string[] | null;
  alreadyCheckedItemMap: Record<string, SelectType>;
  checkedItemMap: Record<string, SelectType>;
  setCheckedItemMap: Dispatch<SetStateAction<Record<string, SelectType>>>;
  refetchBillingProfileDetails?: any;
  billingProfileError?: any;
  refetchEntitlementsResponse: any;
  fetchEntitlementListError: any;
  isFetchEntitlementListLoading: boolean;
  isBillingProfileDetailsLoading: boolean;
  externalProductsList: any;
  isExternalProductListLoading: boolean;
  cloudSiteOrOrgNameMapData: any;
  isSingleProductSelection?: boolean;
  isTWCPhase2Enabled?: boolean;
}

const CheckboxStyles = styled.div`
  label {
    padding: 4px 9px;
  }
`;

const StyledSearchAndFilterContainer = styled.div({
  marginBottom: '20px',
  display: 'flex',
  flexDirection: 'column',
});

const combineMaps = (mapArray: Array<CloudSiteOrOrgNameMap>) => {
  return mapArray.reduce((acc, map) => {
    Object.entries(map).forEach(([key, value]) => {
      if (!acc[key]) {
        // Add new key with its value
        acc[key] = {
          products: [...value.products],
          inactiveProducts: [...value.inactiveProducts],
          isChecked: value.isChecked,
        };
      } else {
        // Merge products arrays for existing keys
        acc[key].products = [...acc[key].products, ...value.products];
        acc[key].inactiveProducts = [...acc[key].inactiveProducts, ...value.inactiveProducts];
      }
    });
    return acc;
  }, {});
};

export const UpdateExistingEntitlementWrapper = ({
  customerIds,
  checkedItemMap,
  setCheckedItemMap,
  alreadyCheckedItemMap,
  refetchBillingProfileDetails,
  billingProfileError,
  refetchEntitlementsResponse,
  fetchEntitlementListError,
  isFetchEntitlementListLoading,
  isBillingProfileDetailsLoading,
  externalProductsList,
  isExternalProductListLoading,
  cloudSiteOrOrgNameMapData,
  isSingleProductSelection = false,
  isTWCPhase2Enabled = false,
}: Props) => {
  const [{ billingProfiles: billingProfilesFromStore, sidebarFilter, featureFlags }] = useStore();
  const { searchQuery } = sidebarFilter ?? {};
  const [, actions] = useCloudSiteOrOrgNameMapStore();

  const { cloudSiteOrOrgNameMap } = cloudSiteOrOrgNameMapData ?? {};
  const cloudSiteOrOrgNameMapArray = (cloudSiteOrOrgNameMap as CloudSiteOrOrgNameMapGroupedByBillingProfile)?.map(
    ({ cloudSiteOrOrgNameMap }) => cloudSiteOrOrgNameMap
  );
  const cloudSiteOrOrgNameMapV2: CloudSiteOrOrgNameMap = combineMaps(cloudSiteOrOrgNameMapArray);
  const sortedCloudSiteOrOrgNameMapV2 = Object.keys(cloudSiteOrOrgNameMapV2)
    .sort()
    .reduce((acc: CloudSiteOrOrgNameMap, key: string) => {
      acc[key] = cloudSiteOrOrgNameMapV2[key];
      return acc;
    }, {} as CloudSiteOrOrgNameMap);

  const activeProductIds = Object.values(sortedCloudSiteOrOrgNameMapV2).flatMap((site) =>
    site.products.map((product) => product.id)
  );

  const [isSelectAllProductsChecked, setIsSelectAllProductsChecked] = useState(false);
  const showInactiveProducts = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_INACTIVE_PRODUCTS_IN_UOF] || false;
  const showAddons = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_ADD_ONS] || false;
  const showSandboxProducts = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_SANDBOX_SUPPORT_FOR_SUBSCRIPTIONS] || false;
  const isSandboxDecouplingEnabled = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_SANDBOX_DECOUPLING] || false;
  const isJPDAsJiraFamilyEnabled = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_JPD_AS_JIRA_FAMILY] || false;
  const isDesignIteration1Enabled = featureFlags?.[STATSIG_FEATURE_GATES.ENABLE_DESIGN_ITERATION_1] || false;

  const { notificationSent, sendSuccessNotification, setNotificationSent } = useNotification();

  const clearSearchQuery = () => {
    actions.setSearchQuery('');
  };

  const handleRefetchEntitlements = () => {
    refetchBillingProfileDetails().then(() => {
      refetchEntitlementsResponse();
    });
  };

  const handleCheckedItems = (type: SelectType, identifier: string) => {
    if (!type || !identifier) return;

    if (type === SelectType.ENTITLEMENT) {
      setCheckedItemMap((prevCheckedItemMap: Record<string, SelectType>) => {
        if (isSingleProductSelection) {
          return { [identifier]: type };
        }
        const newCheckedItemMap = { ...prevCheckedItemMap };
        if (newCheckedItemMap[identifier]) {
          delete newCheckedItemMap[identifier];
        } else {
          newCheckedItemMap[identifier] = type;
        }
        return newCheckedItemMap;
      });
    }
  };

  const showCloudSiteContainer = (() => {
    return customerIds?.length;
  })();

  const triggerErrorNotification = (message = '', header = '') => {
    if (notificationSent) {
      return;
    }
    const notificationOptions = errorNotificationBuilder()
      .isVersion(NotificationTypes.FLAG)
      .withId('transfer-error-notification')
      .withHeader(header)
      .withMessage(message)
      .withDismissAfter(5000)
      .build();
    sendSuccessNotification(notificationOptions);
    setNotificationSent(true);
  };

  if (billingProfileError || fetchEntitlementListError) {
    triggerErrorNotification('Please try again or contact Partner Service Desk.', 'Could not fetch subscriptions');
  }

  const trimSearchQuery = (searchQuery: string) => {
    return processEntity(searchQuery, true).isValid ? searchQuery.trim() : searchQuery;
  };

  const getIsSelectAllChecked = (activeProductIds: string[]): boolean => {
    if (activeProductIds.length > 0) {
      return activeProductIds.every((id) => checkedItemMap[id] || alreadyCheckedItemMap[id]);
    }
    return false;
  };

  const getIsSelectAllDisabled = () => {
    const isProductsAlreadySelected = activeProductIds.every((id) => alreadyCheckedItemMap[id]);
    return isFetchEntitlementListLoading || isBillingProfileDetailsLoading || isProductsAlreadySelected;
  };

  React.useEffect(() => {
    setIsSelectAllProductsChecked(getIsSelectAllChecked(activeProductIds));
  }, [activeProductIds, checkedItemMap]);

  return (
    <section>
      {!!showCloudSiteContainer && (
        <StyledSearchAndFilterContainer>
          <SearchField
            searchQuery={searchQuery}
            onQueryChange={(searchQuery: string) =>
              actions.setSidebarFilter({
                searchQuery: trimSearchQuery(searchQuery),
              })
            }
            clearSearchQuery={clearSearchQuery}
            isDisabled={
              isFetchEntitlementListLoading ||
              isBillingProfileDetailsLoading ||
              billingProfileError ||
              fetchEntitlementListError
            }
            showSearchIconBeforeInput={true}
            showSearchSubtext={true}
            searchSubText={'Search org name, site URL or entitlement ID in the list'}
            placeholder={'Search'}
          />
        </StyledSearchAndFilterContainer>
      )}
      <CheckboxStyles>
        {!isSingleProductSelection &&
          !isEmpty(cloudSiteOrOrgNameMapV2) &&
          !(billingProfileError || fetchEntitlementListError) && (
            <Checkbox
              isChecked={isSelectAllProductsChecked}
              onChange={() => {
                if (isSelectAllProductsChecked) {
                  setCheckedItemMap({});
                } else {
                  Object.keys(cloudSiteOrOrgNameMapV2).forEach((key) => {
                    const products = cloudSiteOrOrgNameMapV2[key].products;
                    products?.map((product: any) => {
                      setCheckedItemMap((prevCheckedItemMap) => ({
                        ...prevCheckedItemMap,
                        [product?.id]: SelectType.ENTITLEMENT,
                      }));
                    });
                  });
                }
                setIsSelectAllProductsChecked(!isSelectAllProductsChecked);
              }}
              label={'Select all'}
              value="Controlled Checkbox"
              name="controlled-checkbox"
              isDisabled={getIsSelectAllDisabled()}
            />
          )}
      </CheckboxStyles>

      {showCloudSiteContainer ? (
        <CloudSiteContainer
          billingProfiles={billingProfilesFromStore}
          cloudSiteOrOrgNameMap={sortedCloudSiteOrOrgNameMapV2}
          productsGroupedByBillingProfileAndCloudSite={null}
          isExternalProductListLoading={isExternalProductListLoading}
          externalProductsList={externalProductsList}
          onChange={handleCheckedItems}
          isLoading={false}
          showErrorState={!!billingProfileError || !!fetchEntitlementListError}
          handleRefetchEntitlements={handleRefetchEntitlements}
          isSelectionDisabled={false}
          isBillingProfileLoading={isBillingProfileDetailsLoading}
          isFetchEntitlementsLoading={isFetchEntitlementListLoading}
          showInactiveProducts={showInactiveProducts}
          shouldShowAddons={showAddons}
          shouldShowSandboxStatus={showSandboxProducts || isSandboxDecouplingEnabled}
          isJPDAsJiraFamilyEnabled={isJPDAsJiraFamilyEnabled}
          isCheckboxSelectionEnabled={isDesignIteration1Enabled}
          alreadyCheckedItemMap={alreadyCheckedItemMap}
          checkedItemMap={checkedItemMap}
          setCheckedItemMap={setCheckedItemMap}
          isSingleProductSelection={isSingleProductSelection}
          isTWCPhase2Enabled={isTWCPhase2Enabled}
        />
      ) : null}
    </section>
  );
};
